import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { normalizeText, normalizeEmail } from '../../utils/normalizeInput';
import { PasswordInput } from '../shared/PasswordInput';
import { POSITIONS } from '../../constants/positions';
import './RegisterPage.css';

export function RegisterPage() {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: 'BD Executive',
    password: '',
    confirmPassword: '',
    // Honeypot: a hidden field bots fill out blindly. If non-empty on submit,
    // we silently no-op the registration. Keeping the field renamed plausibly
    // ("website") improves the odds bots target it.
    website: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid =
    !!formData.name.trim() &&
    !!formData.email.trim() &&
    formData.password.length >= 8 &&
    formData.password === formData.confirmPassword;

  const validateForm = () => {
    if (!formData.name) {
      setError('Name is required');
      return false;
    }
    if (!formData.email) {
      setError('Email is required');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    // Honeypot tripped: pretend it succeeded so the bot doesn't retry, but
    // don't actually create an account.
    if (formData.website) {
      setStep('success');
      return;
    }

    setLoading(true);
    const normalizedEmail = normalizeEmail(formData.email);
    let signedUp = false;
    try {
      // Create the auth user. With email confirmation disabled this returns an
      // active session (we sign it out below). NOTE: it is no longer idempotent —
      // a repeat sign-up of the same email returns "already registered".
      const { error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail,
        password: formData.password,
      });
      if (signUpError) throw signUpError;
      signedUp = true;

      // Record the pending registration. This is the last step before success.
      // If it fails, the auth account exists but the queue row does not. The
      // error branch below (the `signedUp` flag) surfaces a retry message; note
      // that with email confirmation off, a retried signUp returns "already
      // registered" (no longer idempotent), so repeated failures route the user
      // to "contact an administrator".
      const { error: queueError } = await supabase
        .from('registration_queue')
        .insert([
          {
            email: normalizedEmail,
            name: normalizeText(formData.name),
            position: formData.position,
            status: 'pending',
          },
        ]);
      if (queueError) throw queueError;

      // Email confirmation is disabled, so signUp auto-created a session. Sign back
      // out so the registrant lands on the success card, not a logged-in "pending
      // approval" state. Best-effort: a signOut hiccup must not turn a successful
      // registration into an error.
      await supabase.auth.signOut().catch(() => {});

      setStep('success');
    } catch (err) {
      // If the auth account was created but the registration record was not, the
      // user would otherwise be silently locked out. Make the recovery explicit.
      // (Guaranteeing both-or-neither atomically would need a server-side Edge
      // Function — deferred; this keeps the flow recoverable without new infra.)
      if (signedUp) {
        setError(
          'Your account was created, but we could not finish your registration. ' +
          'Please click "Create Account" again to complete it. If it keeps failing, contact an administrator.'
        );
      } else {
        setError(mapSupabaseError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="register-container">
        <div className="success-card">
          <h2>Registration Submitted!</h2>
          <p>
            An administrator will review your registration. You'll be able to log
            in once it's approved.
          </p>
          <button onClick={() => navigate('/login')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="register-card">
        <h1>Join WeGolden</h1>
        <p className="subtitle">Create your account</p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Honeypot: not visible to humans, but bots that fill every field
              will trip it. tabIndex/-1 + autoComplete=off + aria-hidden so
              keyboard and screen-reader users skip past it. */}
          <div className="honeypot" aria-hidden="true">
            <label htmlFor="website">Website (leave blank)</label>
            <input
              id="website"
              name="website"
              type="text"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              required
              disabled={loading}
              autoComplete="name"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@company.com"
              required
              disabled={loading}
              autoComplete="email"
              maxLength={254}
            />
          </div>

          <div className="form-group">
            <label htmlFor="position">Position</label>
            <select
              id="position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              disabled={loading}
            >
              {POSITIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              autoComplete="new-password"
              ariaDescribedBy="password-hint"
            />
            <small id="password-hint">Minimum 8 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
              autoComplete="new-password"
              ariaDescribedBy="confirmPassword-match"
              ariaInvalid={
                !!formData.confirmPassword &&
                !!formData.password &&
                formData.password !== formData.confirmPassword
              }
            />
            <small
              id="confirmPassword-match"
              role="status"
              aria-live="polite"
              className={
                !formData.confirmPassword || !formData.password
                  ? ''
                  : formData.password === formData.confirmPassword
                  ? 'password-match'
                  : 'password-mismatch'
              }
            >
              {!formData.confirmPassword || !formData.password
                ? ' '
                : formData.password === formData.confirmPassword
                ? 'Passwords match'
                : 'Passwords do not match'}
            </small>
          </div>

          {error && <div className="error-message" role="alert">{error}</div>}

          <button type="submit" className="btn-primary" disabled={loading || !isFormValid}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="form-footer">
          Already have an account? <a href="/login">Log in</a>
        </p>
      </div>
    </div>
  );
}
